import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Clipboard } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { CompanySelector } from "../CompanySelector";
import { ContactSelector } from "../ContactSelector";
import { JobFormData, JOB_STATUS_VALUES } from "../types/jobTypes";
import { useTranslation } from "react-i18next";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";

interface BasicInformationTabProps {
  jobData: JobFormData;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, field: keyof JobFormData) => void;
  handleSelectChange: (value: string, field: keyof JobFormData) => void;
  handleSelectCompany: (id: string, name: string) => void;
  handleSelectContact: (contactId: string) => void;
  handlePasteFromClipboard: () => void;
  handleSkillsKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, field: keyof JobFormData) => void;
  handleEmploymentTypeChange: (type: string, checked: boolean) => void;
  isEmploymentTypeSelected: (type: string) => boolean;
}

export const BasicInformationTab = ({
  jobData,
  handleInputChange,
  handleSelectChange,
  handleSelectCompany,
  handleSelectContact,
  handlePasteFromClipboard,
  handleSkillsKeyDown,
  handleEmploymentTypeChange,
  isEmploymentTypeSelected
}: BasicInformationTabProps & { handleSelectChange: (value: string, field: keyof JobFormData) => void }) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4 min-h-[500px] pb-32">
      {/* Hidden input for company ID - required for form validation and submission */}
      <input 
        type="hidden" 
        name="companyId" 
        value={jobData.companyId || ""} 
        required={true}
      />
      
      <div className="flex flex-col md:flex-row md:items-end gap-4">
        <div className="flex-1">
          <Label htmlFor="title" className="text-sm font-medium text-gray-700 mb-1 block">
            {t("jobs.formWizard.jobTitle")} *
          </Label>
          <Input
            id="title"
            type="text"
            value={jobData.title}
            onChange={(e) => handleInputChange(e, "title")}
            placeholder={t("jobs.formWizard.jobTitlePlaceholder")}
            required
          />
        </div>
        <div className="w-full md:w-48">
          <Label htmlFor="status" className="text-sm font-medium text-gray-700 mb-1 block">
            {t("jobs.formWizard.status")}
          </Label>
          <Select
            value={jobData.status || JOB_STATUS_VALUES[0]}
            onValueChange={value => handleSelectChange(value, "status")}
          >
            <SelectTrigger id="status">
              <SelectValue placeholder={t("jobs.formWizard.status")}
                // Show translated value in the select
                >
                {jobData.status ? t(`jobs.formWizard.statuses.${jobData.status}`) : ""}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {JOB_STATUS_VALUES.map((status) => (
                <SelectItem key={status} value={status}>{String(t(`jobs.formWizard.statuses.${status}`, status))}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="company-selector" className="text-sm font-medium text-gray-700 mb-1 block">
            {t("companies.header")} *
          </Label>
          <div id="company-selector">
            <CompanySelector 
              onSelectCompany={handleSelectCompany}
              selectedCompanyId={jobData.companyId}
              required={true}
            />
          </div>
        </div>
        
        <div>
          <Label htmlFor="contact-selector" className="text-sm font-medium text-gray-700 mb-1 block">
            {t("jobs.formWizard.contactPerson")}
          </Label>
          <ContactSelector
            companyId={jobData.companyId}
            onSelectContact={handleSelectContact}
            selectedContactId={jobData.contactId}
            disabled={!jobData.companyId}
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="description" className="text-sm font-medium text-gray-700 mb-1 block">
          {t("jobs.formWizard.jobDescription")}
        </Label>
        <div className="relative">
          <Textarea
            id="description"
            value={jobData.description}
            onChange={(e) => handleInputChange(e, "description")}
            placeholder={t("jobs.formWizard.jobDescriptionPlaceholder")}
            className="min-h-[200px]"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="absolute top-2 right-2"
            onClick={handlePasteFromClipboard}
          >
            <Clipboard className="h-4 w-4 mr-2" />
            {t("jobs.formWizard.paste")}
          </Button>
        </div>
      </div>

      <div>
        <Label htmlFor="aiSummary" className="text-sm font-medium text-gray-700 mb-1 block">
          {t("jobs.formWizard.aiSummary")}
        </Label>
        <Textarea
          id="aiSummary"
          value={jobData.aiSummary}
          onChange={(e) => handleInputChange(e, "aiSummary")}
          placeholder={t("jobs.formWizard.aiSummaryPlaceholder")}
          className="min-h-[100px]"
        />
      </div>
      
      <div>
        <Label htmlFor="skills" className="text-sm font-medium text-gray-700 mb-1 block">
          {t("jobs.formWizard.keySkills")}
        </Label>
        <Input
          id="skills"
          type="text"
          value={jobData.skills}
          onChange={(e) => handleInputChange(e, "skills")}
          onKeyDown={(e) => handleSkillsKeyDown(e, "skills")}
          placeholder={t("jobs.formWizard.keySkillsPlaceholder")}
        />
        {jobData.skills && (
          <div className="mt-2 flex flex-wrap gap-2">
            {(jobData.skills 
              ? Array.isArray(jobData.skills) 
                ? jobData.skills 
                : jobData.skills.split(',').map((skill: string) => skill.trim())
              : []).filter(Boolean).map((skill, index) => (
                <Badge key={index} variant="secondary">
                  {skill}
                </Badge>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
